/**
 * Grupo de checkboxes de permisos por categoría (presentacional + controlado).
 *
 * Pinta el catálogo de permisos agrupado en las 9 categorías del SSR (`progress/
 * explore_equipo_ssr.md`, §B.5), con botones "Todos/Ninguno" por categoría (igual
 * que `manage_permissions.html`). Reutilizable por la gestión de permisos
 * granulares (override de usuario) y por el formulario de roles. No hace `fetch`:
 * recibe el catálogo ya agrupado y el conjunto seleccionado; notifica los cambios.
 *
 * `roleBasePermissions` (opcional) marca con un badge "Rol" los permisos que
 * vienen del rol base del usuario (solo se usa en el override de usuario; en roles
 * no aplica).
 */
import { Button } from "@/components/ui";

import type { ResolvedPermissionCategory } from "../format";

interface PermissionsCheckboxGroupProps {
  /** Catálogo agrupado por categoría (de `groupPermissions`). */
  categories: ResolvedPermissionCategory[];
  /** Ids de permiso actualmente seleccionados. */
  selected: Set<string>;
  /** Ids del rol base (para el badge "Rol"); vacío si no aplica. */
  roleBasePermissions?: Set<string>;
  /** Notifica el nuevo conjunto seleccionado tras un cambio. */
  onChange: (next: Set<string>) => void;
  /** Prefijo de id para los checkboxes (evita colisiones entre usos en la misma página). */
  idPrefix: string;
}

export function PermissionsCheckboxGroup({
  categories,
  selected,
  roleBasePermissions,
  onChange,
  idPrefix,
}: PermissionsCheckboxGroupProps) {
  function toggle(permission: string) {
    const next = new Set(selected);
    if (next.has(permission)) {
      next.delete(permission);
    } else {
      next.add(permission);
    }
    onChange(next);
  }

  function setCategory(category: ResolvedPermissionCategory, checked: boolean) {
    const next = new Set(selected);
    for (const item of category.permissions) {
      if (checked) {
        next.add(item.id);
      } else {
        next.delete(item.id);
      }
    }
    onChange(next);
  }

  return (
    <div className="flex flex-col gap-6">
      {categories.map((category) => (
        <fieldset key={category.id} className="border-l-4 border-brand pl-4">
          <div className="mb-2 flex items-center justify-between gap-2">
            <legend className="text-sm font-semibold text-slate-800">
              {category.label}
            </legend>
            <div className="flex gap-1">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setCategory(category, true)}
              >
                Todos
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setCategory(category, false)}
              >
                Ninguno
              </Button>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {category.permissions.map((item) => {
              const checkboxId = `${idPrefix}-${item.id}`;
              const isRoleBase = roleBasePermissions?.has(item.id) ?? false;
              return (
                <label
                  key={item.id}
                  htmlFor={checkboxId}
                  className="flex items-start gap-2 rounded-md p-1 text-sm text-slate-700 hover:bg-slate-50"
                >
                  <input
                    id={checkboxId}
                    type="checkbox"
                    className="mt-1 h-4 w-4 rounded border-slate-300 text-brand focus:ring-brand"
                    checked={selected.has(item.id)}
                    onChange={() => toggle(item.id)}
                  />
                  <span className="flex flex-col">
                    <span className="font-medium">
                      {item.id}
                      {isRoleBase && (
                        <span className="ml-2 inline-flex items-center rounded bg-sky-100 px-1.5 py-0.5 text-xs font-medium text-sky-700">
                          Rol
                        </span>
                      )}
                    </span>
                    {item.description && (
                      <span className="text-xs text-slate-500">{item.description}</span>
                    )}
                  </span>
                </label>
              );
            })}
          </div>
        </fieldset>
      ))}
    </div>
  );
}
